import React from "react";
import { Play } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import ToolBlock from "./ToolBlock";

const VisionBoardGallery: React.FC = () => {
  return (
    <div className="grid grid-cols-2 gap-3">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="aspect-square cosmic-card overflow-hidden relative"
        >
          <div className="absolute inset-0 bg-cosmic-radial opacity-50" />
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] rounded-full bg-cosmic-500/20 blur-2xl animate-pulse"
            style={{ animationDelay: `${i * 250}ms` }}
          />
          {i === 0 && (
            <div className="absolute inset-0 grid place-items-center">
              <div className="w-12 h-12 rounded-full bg-cosmic-500/20 border border-cosmic-400/40 backdrop-blur-sm grid place-items-center">
                <Play className="h-5 w-5 text-cosmic-200 ml-0.5 fill-current" />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const ToolsSection: React.FC = () => {
  return (
    <section id="tools" className="py-24 md:py-32 px-6 relative overflow-hidden">
      <div className="absolute inset-0 z-[-1] bg-cosmic-radial opacity-30" />

      <div className="container mx-auto max-w-5xl">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <ScrollReveal>
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-wide text-cosmic-200 border border-cosmic-400/30 bg-cosmic-900/50 backdrop-blur-sm mb-4">
              The practice · built on the law
            </span>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-gradient">
              Four ways to write your reality.
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <p className="text-white/70 text-lg leading-relaxed">
              Each tool is a different doorway into the same principle, feel it real now.
              Here's what each one does, and what's happening beneath it the moment you finish.
            </p>
          </ScrollReveal>
        </div>

        <ToolBlock
          verb="Affirm"
          title="DM the Universe"
          description="A guided manifestation chat that walks you through a 13-step affirmation cycle, while a backdrop of your own future plays behind every word you type."
          features={[
            "Set any still or living video from your Vision Board as your backdrop, or generate one on the spot.",
            "Looping animated scenes bring your vision to life as you affirm.",
            "Responses adapt to each step, with strict fidelity to your exact words.",
          ]}
          lawAtWork={
            <>
              You stop asking and start <i>assuming</i>. Speaking in the present tense, with your
              future moving behind the words, you're no longer pleading for the life, you're
              occupying it. This is <i>"believe that ye receive them"</i> made into a daily act.
            </>
          }
          quote="I used to skip affirmations because they felt fake. Watching my actual vision play behind the words changed everything."
          quoteAuthor="Maya R. · daily user, 4 months"
          mediaTag="Demo · Affirming over a living vision"
        />

        <ToolBlock
          verb="Visualise"
          title="Vision Board"
          description="A personal media library of your manifested life. Generate cinematic imagery from a sentence, with the real faces and places from your world woven in."
          features={[
            "Tag people and places you've uploaded, \"my partner Sarah\", \"our future home in Bali\", and watch them appear in your vision.",
            "Bring stills to life as looping videos, then upscale to HD with a face-fix pass that keeps every face true.",
            "Browse it all in a frosted-glass lightbox built for quiet focus.",
          ]}
          lawAtWork={
            <>
              Imagination is the workshop. Here you give the unseen a <i>form</i> your mind can
              accept. When your own face is in the scene, the future stops being an abstract wish
             , you're rehearsing a reality.
            </>
          }
          quote="Seeing my own family in the images, in the home we've only talked about, it stopped feeling like a dream and started feeling like a memory I hadn't had yet."
          quoteAuthor="James T. · daily user, 6 months"
          reverse
          media={<VisionBoardGallery />}
        />

        <ToolBlock
          verb="Embody"
          title="Gratitude Practice"
          description="A full-screen daily ritual that walks you through your Vision Board, one piece at a time. Controls fade. Music plays. You speak gratitude as if it's already here."
          features={[
            "Distraction-free full-screen experience, just you and the image.",
            "Idle-fade controls and ambient soundtrack.",
            "Export your whole gratitude journey as a private video to revisit anytime.",
          ]}
          lawAtWork={
            <>
              Gratitude is the receipt. Saying thank you for what hasn't yet arrived is the
              clearest signal you can send that it has. <i>"If you are grateful, I will surely
              increase you."</i>
            </>
          }
          quote="Ten minutes every morning. By the end I feel like the day already went my way."
          quoteAuthor="Priya S. · daily user, 3 months"
          mediaTag="Demo · The morning gratitude ritual"
        />

        <ToolBlock
          verb="Release"
          title="Mindful Chat"
          description="A gentle, non-clinical space to set the weight down, release what's heavy and shift perspective without analysis."
          features={[
            "Quick mood check-ins to start where you are.",
            "Visual burden-release ritual, worries become bubbles that float away and burst.",
            "Conversation that softens edges instead of dissecting them. Always available, always private.",
          ]}
          lawAtWork={
            <>
              Manifestation runs on signal clarity. When the heavy stuff is held, the channel is
              loud, wanting, lacking, gripping. Setting it down lets the feeling of <i>already
              having</i> come back through clean.
            </>
          }
          quote="It's the only thing I've used that doesn't try to fix me. It just helps me put it down."
          quoteAuthor="Daniel K. · daily user, 5 months"
          reverse
          mediaTag="Demo · Releasing burdens"
        />
      </div>
    </section>
  );
};

export default ToolsSection;
